import { useEffect, useRef } from 'react';

/**
 * Scanner-friendly operator input.
 *
 * Listens for fast-typed character bursts (USB barcode scanners
 * deliver characters at ~1ms per character, with a terminating
 * Enter), debounces them into a single string, and fires the
 * provided callback when the burst ends.
 *
 * Distinguishes scanner input from human typing by the timing
 * threshold: characters arriving within `intercharThresholdMs` of
 * each other are scanner-driven; longer gaps are treated as human
 * typing and ignored.
 *
 * Default suffix character is Enter ('\n' or '\r'); scanners can
 * also be configured to emit Tab — pass `suffix: 'Tab'` to honor
 * that.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * useScannerInput({
 *   onScan: (code) => {
 *     // Look up the serial / lot / WO and navigate
 *     navigate(`/station/serial/${code}`);
 *   },
 * });
 * ```
 *
 * Operators on station, inventory, receiving, quality inspection,
 * maintenance, and batch consoles MUST get scanner-friendly input
 * everywhere they work.
 */

export interface UseScannerInputOptions {
  /**
   * Fired with the assembled scan code (Enter/Tab suffix stripped).
   * Async callbacks are awaited; errors don't crash the listener.
   */
  onScan: (code: string) => void | Promise<void>;
  /**
   * Maximum gap between characters to count them as part of the same
   * scan. Most USB scanners emit characters at <10ms apart; humans
   * are >50ms. Default 30ms balances both. */
  intercharThresholdMs?: number;
  /** Minimum scan length to fire the callback. Default 4. */
  minLength?: number;
  /** Suffix key the scanner emits. Default 'Enter'. */
  suffix?: 'Enter' | 'Tab';
  /**
   * Skip the listener entirely when the user is typing in an input
   * field. Default true; set false on dedicated scan-only screens.
   */
  ignoreWhenInputFocused?: boolean;
}

const isTextInputFocused = (): boolean => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable === true;
};

export const useScannerInput = (options: UseScannerInputOptions): void => {
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    const buffer: string[] = [];
    let lastKeyAt = 0;

    const flush = () => {
      const o = optsRef.current;
      const code = buffer.join('');
      buffer.length = 0;
      lastKeyAt = 0;
      const min = o.minLength ?? 4;
      if (code.length < min) return;
      try {
        const result = o.onScan(code);
        if (result instanceof Promise) {
          result.catch(() => {
            /* host surfaces errors; listener stays armed */
          });
        }
      } catch {
        /* host surfaces errors; listener stays armed */
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const o = optsRef.current;
      if (o.ignoreWhenInputFocused !== false && isTextInputFocused()) return;

      const now = performance.now();
      const threshold = o.intercharThresholdMs ?? 30;
      const suffix = o.suffix ?? 'Enter';

      if (e.key === suffix) {
        if (buffer.length > 0 && now - lastKeyAt < threshold * 4) {
          // Scanner-driven Enter — flush the burst.
          flush();
          e.preventDefault();
        }
        return;
      }

      if (e.key.length !== 1) {
        // Modifier or non-printable; ignore.
        return;
      }

      if (lastKeyAt === 0 || now - lastKeyAt < threshold) {
        buffer.push(e.key);
        lastKeyAt = now;
      } else {
        // Gap exceeds threshold — start a new burst.
        buffer.length = 0;
        buffer.push(e.key);
        lastKeyAt = now;
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);
};
